import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { lazy, Suspense, useEffect, useRef, type ReactNode } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { isAdminUser } from "@/lib/auth-utils";
import { trpc } from "@/lib/trpc";
import { HomeHeroSearch } from "@/components/home-hero-search";
import { HomeHeroSection, HOME_HERO_IMAGE_SRC } from "@/components/home-hero-section";
import { homeHeroLayout } from "@/components/home/home-hero-layout";
import { LiveAuctionHomeSection } from "@/components/auction/live-auction-home-section";
import { cn } from "@/lib/utils";

const HomeFeaturesSection = lazy(() =>
  import("@/components/home-features-section").then((m) => ({ default: m.HomeFeaturesSection })),
);
const HomeBrowseBrandsSection = lazy(() =>
  import("@/components/home/home-browse-brands-section").then((m) => ({
    default: m.HomeBrowseBrandsSection,
  })),
);
const HomeShowcaseSection = lazy(() =>
  import("@/components/home/home-showcase-section").then((m) => ({
    default: m.HomeShowcaseSection,
  })),
);
const BoostedCarsSection = lazy(() =>
  import("@/components/home/boosted-cars-section").then((m) => ({ default: m.BoostedCarsSection })),
);
const InsuranceComparisonSection = lazy(() =>
  import("@/components/insurance/insurance-comparison-section").then((m) => ({
    default: m.InsuranceComparisonSection,
  })),
);

function DeferredSection({ children }: { children: ReactNode }) {
  return <Suspense fallback={null}>{children}</Suspense>;
}

export default function Home() {
  const { isAuthenticated, user } = useAuth();
  const auctionsTimelineRef = useRef<HTMLElement>(null);
  const utils = trpc.useUtils();

  useEffect(() => {
    const href = HOME_HERO_IMAGE_SRC;
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = href;
    document.head.appendChild(link);

    const prefetchListings = () => {
      void import("./CarListings");
      void utils.cars.listWithTotal.prefetch({ limit: 12, offset: 0 });
    };
    const usedIdle = "requestIdleCallback" in window;
    const idleId = usedIdle
      ? window.requestIdleCallback(prefetchListings)
      : window.setTimeout(prefetchListings, 1200);

    return () => {
      link.remove();
      if (usedIdle) window.cancelIdleCallback(idleId);
      else window.clearTimeout(idleId);
    };
  }, [utils]);
  const { data: flags } = trpc.settings.featureFlags.useQuery(undefined, { staleTime: 60_000 });
  const { data: liveAuctions, isLoading: spotlightLoading } = trpc.cars.liveAuctionSpotlight.useQuery(
    { limit: 8 },
    { staleTime: 60_000 }
  );

  return (
    <div className="w-full">
      {/* Search-first landing: full-bleed hero + overlapping search card */}
      <div className="relative bg-white">
        <HomeHeroSection
          isAuthenticated={isAuthenticated}
          showAddCar={isAuthenticated && isAdminUser(user)}
        />

        <div className={cn(homeHeroLayout.searchOverlap, "-mt-32 sm:-mt-36 md:-mt-40")}>
          <div className={cn(homeHeroLayout.searchWidth, "w-[90%] max-w-7xl")}>
            <HomeHeroSearch />
          </div>
        </div>
      </div>

      <DeferredSection>
        <HomeFeaturesSection />
      </DeferredSection>

      <div className="landing-deferred-section">
        <DeferredSection>
          <HomeBrowseBrandsSection />
        </DeferredSection>
      </div>

      <div className="landing-deferred-section">
        <DeferredSection>
          <HomeShowcaseSection />
        </DeferredSection>
      </div>

      {flags?.showBoostSection !== false ? (
        <div className="landing-deferred-section">
          <DeferredSection>
            <BoostedCarsSection />
          </DeferredSection>
        </div>
      ) : null}

      {flags?.showInsuranceLanding !== false ? (
        <div className="landing-deferred-section">
          <DeferredSection>
            <InsuranceComparisonSection />
          </DeferredSection>
        </div>
      ) : null}

      <LiveAuctionHomeSection
        liveAuctions={liveAuctions}
        loading={spotlightLoading}
        timelineRef={auctionsTimelineRef}
      />

      {/* CTA Section */}
      <section className="relative w-full overflow-hidden py-12 md:py-16">
        {/* Background Image · scaled down slightly so cars read clearer behind text */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <img
            src={HOME_HERO_IMAGE_SRC}
            alt=""
            width={1920}
            height={1080}
            loading="lazy"
            decoding="async"
            className="size-full scale-110 object-cover object-[50%_58%]"
          />
          <div className="absolute inset-0 bg-linear-to-b from-background/85 via-background/65 to-background/85" />
        </div>
        <div className="container relative z-10 text-center">
          <h2 className="mb-4 text-3xl font-bold md:text-4xl">هل أنت مستعد للبدء؟</h2>
          <p className="mx-auto mb-6 max-w-2xl text-base text-muted-foreground md:text-lg">
            ابدأ معنا ← ابحث، قارن، واشترِ أو بع سيارتك بثقة على منصة واحدة
          </p>
          {!isAuthenticated ? (
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button
                asChild
                size="lg"
                className="bg-accent text-accent-foreground hover:opacity-90"
              >
                <Link href="/login">تسجيل الدخول الآن</Link>
              </Button>
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}
